import { addressSchema } from '@commenda/schema/address';
import { EducationalQualificationEnum } from '@commenda/types/src/random';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { Country } from '@prisma/client';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as z from 'zod';
import { fileSchema } from '../../../schema/base';

export const companyInfoSchema = z
  .object({
    corporationName: z
      .string()
      .trim()
      .min(1, { message: 'Corporation name should not be empty.' }),
    companyLegalName2: z
      .string()
      .trim()
      .min(1, { message: 'Corporation name should not be empty.' }),
    companyLegalName3: z
      .string()
      .trim()
      .min(1, { message: 'Corporation name should not be empty.' }),
    contactPhoneNumber: z
      .string()
      .trim()
      .refine((value) => isValidPhoneNumber(value), {
        message: 'Please enter a valid phone number',
      }),
    contactEmail: z
      .string()
      .trim()
      .email({ message: 'Please enter a valid email address' }),
    businessDescription: z.string().trim().min(25, {
      message: 'Please enter a description with at least 25 characters.',
    }),
    industry: z
      .string()
      .trim()
      .min(1, { message: 'Please select your industry.' }),
    customIndustry: z.string().trim().optional(),
    companyAddressProof: fileSchema.nullable().optional(),
    isOnRentedProperty: z.boolean(),
    rentalAgreement: fileSchema.nullable().optional(),
    isVirtualAddress: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.industry === 'Other') {
      if (!data.customIndustry?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter the industry.',
          path: ['customIndustry'],
        });
      }
    }

    if (!data.isVirtualAddress) {
      if (!data.companyAddressProof?.fileUrl) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please upload utility bill for registered address.',
          path: ['companyAddressProof'],
        });
      }
    }

    // Skip rental agreement validation if virtual address is selected
    if (
      !data.isVirtualAddress &&
      data.isOnRentedProperty &&
      !data.rentalAgreement
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please upload your rental agreement.',
        path: ['rentalAgreement'],
      });
    }
  });

export const directorSchema = z
  .object({
    directorFirstName: z
      .string()
      .trim()
      .min(1, { message: 'Please enter a valid first name' }),
    directorLastName: z.string().trim().nullable(),
    number: z
      .string()
      .trim()
      .refine((value) => isValidPhoneNumber(value), {
        message: 'Please enter a valid phone number',
      }),
    email: z
      .string()
      .trim()
      .email({ message: 'Please enter a valid email address' }),
    operatingAddress: addressSchema,
    hasDIN: z.boolean().default(false),
    addressProof: fileSchema,
    panCard: fileSchema.optional().nullable(),
    aadharCard: fileSchema.optional().nullable(),
    identityProof: fileSchema,
    specimenSignature: fileSchema,
    passportSizePhoto: fileSchema,
    directorIdentificationNumber: z.string().trim().optional().nullable(),
    personalDetails: z.object({
      hasDSC: z.boolean().default(false),
      nationality: z.string().min(1, { message: 'Please select nationality' }),
      educationalQualification: z.nativeEnum(EducationalQualificationEnum, {
        errorMap: () => {
          return { message: 'Please select educational qualification' };
        },
      }),
      occupation: z
        .string()
        .trim()
        .min(1, { message: 'Please enter your occupation.' }),
      placeOfBirth: z.object({
        country: z.string().min(1, { message: 'Please select a country' }),
        state: z.string().min(1, { message: 'Please select a state' }),
        district: z
          .string()
          .trim()
          .min(1, { message: 'Please enter the district' }),
      }),
      dateOfOccupancyInCurrentAddress: z
        .string()
        .min(1, { message: 'Please enter a valid date' })
        .refine(
          (value) => {
            if (!value) return false;
            const currentDate = NonTimezoneSensitiveDate.fromNativeDate(
              new Date()
            );
            const nonTimeZoneSensitiveDate =
              NonTimezoneSensitiveDate.fromNativeDate(new Date(value));
            return currentDate.gte(nonTimeZoneSensitiveDate);
          },
          { message: 'Date of occupancy must not be in the future.' }
        ),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.hasDIN) {
      if (!/^[0-9]{8}$/.test(data.directorIdentificationNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Please input a valid 8-digit director identification number.',
          path: ['directorIdentificationNumber'],
        });
      }
    }
    if (data.personalDetails.nationality === Country.IN) {
      if (!data.aadharCard) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please upload a valid aadhar card.',
          path: ['aadharCard'],
        });
      }
      if (!data.panCard) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please upload a valid pan card.',
          path: ['panCard'],
        });
      }
    }
  });

export type CompanyInfoFormFields = z.infer<typeof companyInfoSchema>;
export type DirectorSchemaFields = z.infer<typeof directorSchema>;
